import { createOS, getStatusCode } from '../services/osService';
import { removeMask } from '../utils/formatters';
import { formatLocationLabel, formatSiteAddressFromRaw } from '../utils/openOsV2SearchBridge';
import { getApiBaseUrl } from '../../../utils/apiConfig';

const API_BASE_URL = getApiBaseUrl();

function loggedUser() {
  try {
    return JSON.parse(localStorage.getItem('userData') || 'null') || {};
  } catch {
    return {};
  }
}

export function getOpenOsV2Actor() {
  const user = loggedUser();
  const userName = user.username || user.name || '';
  const userEmail = user.email || '';
  const nomeUsuario = userEmail && userEmail.includes('@')
    ? userEmail.split('@')[0]
    : userName;
  return { nomeUsuario, userId: user.id || null };
}

function pickCodTec(agenda, raw) {
  const tech = agenda && agenda.tecnicoObj;
  const fromAgenda = tech && (tech.id_tech || tech.id);
  if (fromAgenda != null && String(fromAgenda).trim() !== '') {
    return String(fromAgenda).padStart(4, '0');
  }
  if (raw && raw.codTec) return raw.codTec;
  return null;
}

function parseCounter(value) {
  const n = parseInt(String(value || '').replace(/\D/g, ''), 10);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Monta o body de POST /api/os/create no mesmo formato do Open OS v1.
 */
export function buildOpenOsV2Payload({
  origem,
  tipoOsValue,
  form,
  loc,
  raw,
  serie,
  counters,
  agenda,
  actor,
  codStatus,
}) {
  const r = raw || {};
  const isEstoque = origem === 'estoque';
  const fone1 = String((form && form.contatoFone1) || '').trim();
  const fone2 = String((form && form.contatoFone2) || '').trim();
  const foneWhats = String((form && form.contatoWhatsApp) || '').trim();
  const email = String((form && form.email) || '').trim();
  const confirmedCounters = !!(counters && counters.confirmed);
  const scheduleType = agenda && agenda.scheduleType ? agenda.scheduleType : null;

  return {
    NOME_USUARIO: actor.nomeUsuario,
    CONTRATO: r.codigo || '',
    CODCLI: r.codCli || '',
    CODPROD: r.codprod || r.codProduto || '',
    SERIE: r.serie || serie || '',
    ATENDENTE: actor.nomeUsuario,
    SOLICITANTE: String((form && form.solicitante) || '').trim(),
    CODEMP: r.codEmp || '',
    MOTIVO_OBS_DEFEITO: String((form && form.obs) || '').trim(),
    CODTEC: pickCodTec(agenda, r),
    CONTADOR_PB: confirmedCounters ? parseCounter(counters.pb) : 0,
    CONTADOR_COR: confirmedCounters ? parseCounter(counters.cor) : 0,
    CONTADOR_TOTAL: confirmedCounters ? parseCounter(counters.total) : 0,
    END_LOGRADOURO: loc.END_LOGRADOURO || '',
    END_NUM: loc.END_NUM || '0',
    END_LOGRADOURO_COMP: loc.END_LOGRADOURO_COMP || '',
    END_BAIRRO: loc.END_BAIRRO || '',
    END_CIDADE: loc.END_CIDADE || '',
    END_ESTADO: loc.END_ESTADO || '',
    END_CEP: loc.END_CEP || '',
    FONE: removeMask(fone1 || r.end_fone || ''),
    EMAIL: email || null,
    CODSITE: r.codSite || '',
    CONTATO: fone1,
    CONTATO_FONE1: fone1,
    CONTATO_WHATSAPP: foneWhats,
    CONTATO_FONE2: fone2,
    LOCALIZACAO: loc.LOCALIZACAO || '',
    CODIGO_DEFEITO: String((form && form.defeitoCodigo) || '').trim(),
    CODSTATUS: codStatus,
    ORIGEM: isEstoque ? 'E' : 'C',
    TIPOOS: isEstoque ? (tipoOsValue || 'E') : (tipoOsValue || 'N'),
    FONE2: removeMask(fone2),
    FONEWHATS: removeMask(foneWhats),
    scheduleType: scheduleType,
    scheduleDate: agenda && agenda.noCertainDate ? null : ((agenda && (agenda.dataIso || agenda.data)) || null),
    scheduleTechnicianId: (agenda && agenda.tecnicoObj && (agenda.tecnicoObj.id_tech || agenda.tecnicoObj.id))
      || (agenda && agenda.tecnicoId)
      || null,
    standbyNotes: agenda && agenda.noCertainDate ? (agenda.standbyNotes || null) : null,
    userId: actor.userId,
  };
}

export async function submitOpenOsV2Orders({
  origem,
  tipoOsValue,
  form,
  loc,
  counters,
  agenda,
  items,
}) {
  const actor = getOpenOsV2Actor();
  const isEstoque = origem === 'estoque';
  const scheduleType = agenda && agenda.scheduleType ? agenda.scheduleType : null;
  const osType = isEstoque ? 'estoque' : 'contrato';
  const tipoOsForStatus = isEstoque ? null : (tipoOsValue || 'N');
  const statusResult = await getStatusCode(scheduleType, osType, tipoOsForStatus);
  if (!statusResult.success || !statusResult.codStatus) {
    throw new Error('Não foi possível obter o código de status');
  }

  const lista = [];
  for (let i = 0; i < items.length; i += 1) {
    const it = items[i];
    const osData = buildOpenOsV2Payload({
      origem,
      tipoOsValue,
      form,
      loc: it.loc || loc,
      raw: it.raw,
      serie: it.serie,
      counters,
      agenda,
      actor,
      codStatus: statusResult.codStatus,
    });
    const result = await createOS(osData);
    const ok = !!(result && result.success && result.osNumber);
    const itemLoc = it.loc || loc;
    const enderecoLabel = formatLocationLabel(itemLoc)
      || formatSiteAddressFromRaw(it.raw)
      || (it.raw && typeof it.raw.Endereco === 'string' ? it.raw.Endereco : '')
      || '';
    lista.push({
      numero: ok ? String(result.osNumber) : '—',
      modelo: it.modelo,
      serie: it.serie,
      patrimonio: it.patrimonio,
      codEmp: (it.raw && it.raw.codEmp) || '',
      local: it.local || (it.raw && it.raw.Endereco_Local) || itemLoc.LOCALIZACAO || '',
      endereco: enderecoLabel,
      linha: it.linha,
      tipoOsValue,
      LOCALIZACAO: itemLoc.LOCALIZACAO,
      END_LOGRADOURO: itemLoc.END_LOGRADOURO,
      END_NUM: itemLoc.END_NUM,
      END_LOGRADOURO_COMP: itemLoc.END_LOGRADOURO_COMP,
      END_BAIRRO: itemLoc.END_BAIRRO,
      END_CIDADE: itemLoc.END_CIDADE,
      END_ESTADO: itemLoc.END_ESTADO,
      END_CEP: itemLoc.END_CEP,
      success: ok,
      error: ok
        ? null
        : ((result && result.errors && result.errors[0] && result.errors[0].ErrorMessage) || 'Erro ao criar ordem de serviço'),
    });
  }

  const scheduleTypeVal = scheduleType;
  const isApenasTecnico = scheduleTypeVal === 'apenas_tecnico';
  const wasScheduled = !!(scheduleTypeVal && agenda && (agenda.tecnicoObj || agenda.tecnicoId) && !isApenasTecnico);
  const technicianId = wasScheduled
    ? (agenda.tecnicoObj && (agenda.tecnicoObj.id_tech || agenda.tecnicoObj.id)) || agenda.tecnicoId
    : null;

  return { lista, scheduled: wasScheduled, technicianId };
}

/**
 * Programa preventivas (OS múltipla) para geração futura — POST /api/os/schedule-preventive.
 */
export async function submitSchedulePreventiveOrders({
  origem,
  tipoOsValue,
  form,
  loc,
  counters,
  agenda,
  items,
}) {
  const actor = getOpenOsV2Actor();
  const scheduleType = agenda && agenda.scheduleType ? agenda.scheduleType : null;
  if (scheduleType !== 'tomorrow' && scheduleType !== 'future') {
    throw new Error('Programar só é válido para Amanhã ou Futura (com data)');
  }
  if (agenda && agenda.noCertainDate) {
    throw new Error('Informe uma data de previsão para programar');
  }

  const scheduleDate = (agenda && (agenda.dataIso || agenda.data)) || null;
  if (!scheduleDate) {
    throw new Error('Data de agendamento obrigatória');
  }

  const statusResult = await getStatusCode(scheduleType, 'contrato', tipoOsValue || 'S');
  if (!statusResult.success || !statusResult.codStatus) {
    throw new Error('Não foi possível obter o código de status');
  }

  const payloadItems = (items || []).map((it) => {
    const payload = buildOpenOsV2Payload({
      origem,
      tipoOsValue: tipoOsValue || 'S',
      form,
      loc: it.loc || loc,
      raw: it.raw,
      serie: it.serie,
      counters,
      agenda,
      actor,
      codStatus: statusResult.codStatus,
    });
    return {
      serie: it.serie || payload.SERIE,
      patrimonio: it.patrimonio || '',
      modelo: it.modelo || '',
      clienteNome: (it.raw && (it.raw.cliente || it.raw.clienteNome || it.raw.CLIENTE)) || '',
      payload,
    };
  });

  const token = localStorage.getItem('authToken');
  const tech = agenda && agenda.tecnicoObj;
  const codTec = tech && (tech.id_tech || tech.id);
  const res = await fetch(`${API_BASE_URL}/api/os/schedule-preventive`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      scheduleDate,
      scheduleType,
      codTec: codTec != null ? String(codTec).padStart(4, '0') : null,
      techName: (agenda && agenda.tecnico) || '',
      origem: origem === 'estoque' ? 'E' : 'C',
      solicitante: form && form.solicitante,
      contatoFone1: form && form.contatoFone1,
      contatoWhatsapp: form && form.contatoWhatsApp,
      contatoFone2: form && form.contatoFone2,
      email: form && form.email,
      defeitoCodigo: form && form.defeitoCodigo,
      motivoObs: form && form.obs,
      items: payloadItems,
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) {
    throw new Error(data.message || `Erro HTTP ${res.status}`);
  }

  const lista = payloadItems.map((it) => ({
    numero: 'agendada',
    modelo: it.modelo,
    serie: it.serie,
    patrimonio: it.patrimonio,
    linha: `Preventiva programada · ${agenda.tecnico || '—'} em ${agenda.data || scheduleDate}`,
    success: true,
    scheduled: true,
  }));

  return {
    lista,
    scheduled: true,
    programmed: true,
    batchId: data.data && data.data.batchId,
    scheduleDate: data.data && data.data.scheduleDate,
    technicianId: codTec || null,
  };
}

/**
 * Lista preventivas agendadas (Roadmap).
 */
export async function fetchScheduledPreventives({ from, to, status } = {}) {
  const token = localStorage.getItem('authToken');
  const qs = new URLSearchParams();
  if (from) qs.set('from', from);
  if (to) qs.set('to', to);
  if (status) qs.set('status', status);
  const res = await fetch(`${API_BASE_URL}/api/os/schedule-preventive?${qs}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) {
    throw new Error(data.message || `Erro HTTP ${res.status}`);
  }
  return (data.data && data.data.items) || [];
}
